import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Flower2, Send, Clock, Loader2, AlertCircle, RefreshCw, 
  MessageCircle, ArrowLeft, Timer
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AriaChatPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [sessionValid, setSessionValid] = useState(false);
  const [remainingTime, setRemainingTime] = useState(1800); // 30 min in seconds
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [streamingMessage, setStreamingMessage] = useState('');
  
  const messagesEndRef = useRef(null);
  const timerRef = useRef(null);

  // Verify session on mount
  useEffect(() => {
    if (!sessionId) {
      navigate('/aria');
      return;
    }
    verifySession();
  }, [sessionId, navigate]);

  // Timer countdown
  useEffect(() => {
    if (sessionValid && remainingTime > 0) {
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setSessionValid(false);
            toast.error('Votre session est terminée');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionValid]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingMessage]);

  const verifySession = async () => {
    try {
      const response = await axios.get(`${API}/aria/verify-session/${sessionId}`);
      
      if (response.data.valid) {
        setSessionValid(true);
        setRemainingTime(response.data.remaining_seconds || 1800);
        
        // Load existing messages
        await loadMessages();
        
        // If no messages, add welcome message from Aria
        if (messages.length === 0) {
          setMessages([{
            id: 'welcome',
            role: 'assistant',
            content: "Bonjour ! Je suis Aria, votre conseillère bien-être virtuelle. 🌸\n\nJe suis là pour vous accompagner et vous aider à vous sentir mieux. Que ce soit pour gérer le stress, l'anxiété, les maux de tête ou améliorer votre sommeil, je suis à votre écoute.\n\nComment vous sentez-vous aujourd'hui ?",
            created_at: new Date().toISOString()
          }]);
        }
      } else {
        toast.error('Session invalide ou expirée');
        navigate('/aria');
      }
    } catch (error) {
      console.error('Session verification error:', error);
      toast.error(error.response?.data?.detail || 'Erreur de vérification');
      navigate('/aria');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/aria/session/${sessionId}/messages`);
      if (response.data.length > 0) {
        setMessages(response.data);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;
    
    const userMessage = inputMessage.trim();
    setInputMessage('');
    setSending(true);
    setStreamingMessage('');
    
    // Add user message immediately
    const userMsg = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);
    
    try {
      const response = await fetch(`${API}/aria/session/${sessionId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erreur de communication');
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.type === 'delta') {
                fullResponse += data.content;
                setStreamingMessage(fullResponse);
              } else if (data.type === 'done') {
                // Add complete message
                setMessages(prev => [...prev, {
                  id: data.message_id,
                  role: 'assistant',
                  content: fullResponse,
                  created_at: new Date().toISOString()
                }]);
                setStreamingMessage('');
              } else if (data.type === 'error') {
                toast.error(data.message);
              }
            } catch (e) {
              // Ignore parsing errors for incomplete chunks
            }
          }
        }
      }
    } catch (error) {
      console.error('Send message error:', error);
      toast.error(error.message || 'Erreur lors de l\'envoi');
      // Remove optimistic user message on error
      setMessages(prev => prev.filter(m => m.id !== userMsg.id));
    } finally {
      setSending(false);
      setStreamingMessage('');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (remainingTime <= 60) return 'text-red-600 bg-red-100';
    if (remainingTime <= 300) return 'text-amber-600 bg-amber-100';
    return 'text-green-600 bg-green-100';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600 mb-4" />
          <p className="text-muted-foreground">Vérification de la session...</p>
        </div>
      </div>
    );
  }

  if (!sessionValid) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container-app py-20">
          <div className="max-w-md mx-auto text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-semibold mb-4">Session expirée</h1>
            <p className="text-muted-foreground mb-6">
              Votre session de 30 minutes est terminée.
            </p>
            <Link to="/aria">
              <Button className="bg-purple-600 hover:bg-purple-700">
                <RefreshCw className="w-4 h-4 mr-2" />
                Nouvelle session
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col">
      <Navbar />
      
      {/* Chat Header */}
      <div className="bg-white border-b border-purple-100 sticky top-0 z-10">
        <div className="container-app py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/aria" className="text-muted-foreground hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                <Flower2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="font-semibold">Aria</h1>
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                  En ligne
                </p>
              </div>
            </div>
            
            {/* Timer */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${getTimerColor()}`}>
              <Timer className="w-4 h-4" />
              <span className="font-mono font-medium">{formatTime(remainingTime)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="container-app py-4 space-y-4">
          {messages.map((msg) => (
            <div 
              key={msg.id}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] ${
                msg.role === 'user' 
                  ? 'bg-purple-600 text-white rounded-2xl rounded-br-md' 
                  : 'bg-white border border-purple-100 rounded-2xl rounded-bl-md shadow-sm'
              } px-4 py-3`}>
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-purple-50">
                    <Flower2 className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-medium text-purple-600">Aria</span>
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              </div>
            </div>
          ))}
          
          {/* Streaming message */}
          {streamingMessage && (
            <div className="flex justify-start">
              <div className="max-w-[80%] bg-white border border-purple-100 rounded-2xl rounded-bl-md shadow-sm px-4 py-3">
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-purple-50">
                  <Flower2 className="w-4 h-4 text-purple-500" />
                  <span className="text-xs font-medium text-purple-600">Aria</span>
                </div>
                <p className="text-sm whitespace-pre-wrap">{streamingMessage}</p>
                <span className="inline-block w-2 h-4 bg-purple-400 animate-pulse ml-1"></span>
              </div>
            </div>
          )}
          
          {/* Typing indicator */}
          {sending && !streamingMessage && (
            <div className="flex justify-start">
              <div className="bg-white border border-purple-100 rounded-2xl px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <Flower2 className="w-4 h-4 text-purple-500" />
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="bg-white border-t border-purple-100 p-4">
        <div className="container-app">
          <div className="flex gap-3">
            <Textarea
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Écrivez votre message..."
              className="flex-1 min-h-[50px] max-h-[150px] resize-none rounded-xl border-purple-200 focus:border-purple-400"
              disabled={sending || remainingTime <= 0}
              data-testid="aria-chat-input"
            />
            <Button 
              onClick={sendMessage}
              disabled={!inputMessage.trim() || sending || remainingTime <= 0}
              className="h-[50px] w-[50px] bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-xl"
              data-testid="aria-send-btn"
            >
              {sending ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
          
          {remainingTime <= 60 && remainingTime > 0 && (
            <p className="text-xs text-red-600 mt-2 text-center">
              ⚠️ Moins d'une minute restante !
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AriaChatPage;
